import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import FiltersBar, { useFilterParams } from "../components/Filters";
import { useAuth } from "../context/AuthContext";
import { PublicJobCard } from "./Landing";
import { VEHICLE_LABELS } from "../components/bits";
import { COUNTRIES } from "../constants";
import { List, Map as MapIcon, Lock } from "lucide-react";

const SORTS = [["pickup_date", "Pickup date"], ["price_asc", "Price ↑"], ["price_desc", "Price ↓"], ["distance", "Nearest"]];

function JoinGate() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid="find-jobs-gate">
      <Seo title="Find courier jobs in Europe | CourierRadar"
        description="Open courier and transport jobs across the UK and Europe. Join CourierRadar as an approved carrier to view live loads, bid your price and get paid with a confirmed invoice trail."
        canonical="https://courierradar.com/find-jobs" />
      <span className="w-12 h-12 rounded-2xl bg-[#1C1B1A] flex items-center justify-center mb-5"><Lock className="w-5 h-5 text-[#F2D06B]" /></span>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3">Courier jobs across Europe — for members</h1>
      <p className="text-sm text-[#78756E] leading-relaxed max-w-xl mb-3">
        CourierRadar is a closed, verified marketplace: every open load comes from a document-checked company, and every
        carrier is approved before they can see it. That keeps rates fair, cargo safe and time-wasters out.
      </p>
      <p className="text-sm text-[#78756E] leading-relaxed max-w-xl mb-6">
        Approved carriers get the full map-first load board — same-day hotshots, dedicated routes and multi-drop work
        across the UK and Europe — with live distance, cargo and payout details on every job.
      </p>
      <div className="flex flex-wrap gap-3">
        <Link to="/auth?mode=register&role=courier" data-testid="gate-join-courier" className="cr-btn-dark">Join as a carrier</Link>
        <Link to="/auth" data-testid="gate-signin" className="cr-btn-yellow">Sign in</Link>
      </div>
      <p className="text-[11px] text-[#A09D95] mt-6">Company looking to post? <Link to="/shippers" className="underline underline-offset-2 font-semibold text-[#1C1B1A]">See how shipping works</Link>.</p>
    </div>
  );
}

export default function FindJobs() {
  const { user } = useAuth();
  const [params, setParam, clearAll] = useFilterParams();
  const [jobs, setJobs] = useState(null);
  const [total, setTotal] = useState(0);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);

  const canBrowse = !!user && (user.role === "admin"
    || (user.role === "courier" && user.status === "approved" && user.subscription_status === "active"));

  useEffect(() => {
    const from = params.get("from");
    const to = params.get("to");
    if ((from || to) && !params.get("q")) {
      setParam({ q: [from, to].filter(Boolean).join(" "), from: null, to: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = [
    { key: "q", label: "Route, city or ref", type: "text", short: "Search", className: "w-44" },
    { key: "country", label: "Country", type: "select", options: COUNTRIES },
    { key: "vehicle_type", label: "Vehicle", type: "select", options: Object.entries(VEHICLE_LABELS) },
    { key: "date_from", label: "From date", type: "date", short: "From" },
    { key: "near", label: "Near a place…", type: "near" },
    { key: "radius_km", label: "Radius km", type: "number", short: "Radius" },
    { key: "min_price", label: "Min €", type: "number", short: "Min" },
    { key: "max_price", label: "Max €", type: "number", short: "Max" },
  ];

  const load = useCallback(async () => {
    if (!canBrowse) return;
    try {
      const p = Object.fromEntries(params.entries());
      const query = {};
      Object.entries(p).forEach(([k, v]) => {
        if (["min_price", "max_price", "radius_km", "near_lat", "near_lng"].includes(k)) query[k] = parseFloat(v);
        else if (k !== "near_label") query[k] = v;
      });
      const { data } = await api.get("/public/jobs/search", { params: query });
      setJobs(data.jobs);
      setTotal(data.total);
    } catch { setJobs([]); }
  }, [params, canBrowse]);

  useEffect(() => { load(); }, [load]);

  if (!user || !canBrowse) return <JoinGate />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="find-jobs-page">
      <Seo title="Find courier jobs in Europe | CourierRadar"
        description="Search open courier and transport jobs across the UK and Europe. Filter by route, vehicle and date."
        canonical="https://courierradar.com/find-jobs" />
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-5">Find jobs</h1>

      <div className="-mx-4 sm:-mx-6">
        <FiltersBar fields={fields} params={params} setParam={setParam} clearAll={clearAll}
          sortOptions={SORTS} total={jobs === null ? null : total} />
      </div>

      <div className="flex items-center justify-end mt-4 mb-3">
        <div className="flex gap-1 bg-zinc-100 p-0.5 rounded-lg">
          <button data-testid="view-list" onClick={() => setView("list")} className={`p-1.5 rounded-md ${view === "list" ? "bg-white shadow-sm" : ""}`}><List className="w-4 h-4" /></button>
          <button data-testid="view-map" onClick={() => setView("map")} className={`p-1.5 rounded-md ${view === "map" ? "bg-white shadow-sm" : ""}`}><MapIcon className="w-4 h-4" /></button>
        </div>
      </div>

      {jobs && jobs.length === 0 && (
        <p data-testid="no-results" className="text-sm text-zinc-400 border border-dashed border-zinc-200 rounded-xl p-10 text-center">
          No jobs match this search right now. Widen the filters or check back soon.
        </p>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className={`space-y-2.5 ${view === "map" ? "hidden lg:block" : ""}`} data-testid="results-list">
          {(jobs || []).map((j) => (
            <div key={j.ref} onClick={() => setSelected(j.id)}>
              <PublicJobCard job={j} />
              {j.distance_from_search_km != null && (
                <p className="text-[10px] text-zinc-400 pl-1 pt-0.5">{j.distance_from_search_km} km from search point</p>
              )}
            </div>
          ))}
        </div>
        <div className={`rounded-xl overflow-hidden border border-zinc-200 h-[60vh] ${view === "list" ? "hidden lg:block" : ""}`} data-testid="results-map">
          <MapView jobs={jobs || []} selectedId={selected} height="100%" />
        </div>
      </div>
    </div>
  );
}
